import { useEffect, useMemo, useRef, useState } from 'react'

import ProfilePhoto from 'src/shared/assets/images/profile-avatar-fallback.png'
import TruncatedText from 'src/shared/components/ui/miscellaneous/truncated-text'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useContainerWidth } from 'src/shared/hooks/use-container-width'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import type { UserZillowReview } from '../../../api/marketplace/use-user-reviews'
import { MiniZillow } from '../../../assets/mini-zillow'
import { ReviewStar } from '../../../assets/review-star'
import { ProfileAvatar } from '../../profile-photos/profile-avatar'
import { getNumberOfStars } from '../util/get-number-of-stars'

interface PublicPageReviewCardProps {
  review: UserZillowReview
  // TODO: Remove default when we are sure all the old ui can be removed.
  type?: 'default' | 'section'
}

const PublicPageReviewCard = ({ review, type = 'default' }: PublicPageReviewCardProps) => {
  const [descriptionOverflow, setDescriptionOverflow] = useState(false)
  const [expandDescription, setExpandDescription] = useState(false)
  const { isMobile } = useBreakpoint()
  const descriptionBoxRef = useRef<HTMLDivElement>(null)
  const containerWidth = useContainerWidth({
    containerElement: descriptionBoxRef,
  })

  useEffect(() => {
    checkDescriptionOverflow()
  }, [containerWidth, expandDescription])

  const checkDescriptionOverflow = () => {
    const container = descriptionBoxRef.current
    if (container) {
      const overflow = container.scrollHeight > container.clientHeight
      setDescriptionOverflow(overflow)
    }
  }

  const onExpand = () => {
    setExpandDescription(true)
  }

  const onCollapse = () => {
    setExpandDescription(false)
  }

  const descriptionText = useMemo(() => {
    if (!review.serviceDescription) return ''

    if (!review.locationZipcode) return review.serviceDescription

    return `${review.serviceDescription} in ${review.locationZipcode}`
  }, [review.serviceDescription, review.locationZipcode])

  const dateText = useMemo(() => {
    if (!review.date) return ''
    // return new Date(review.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    return formatMessageTime(review.date)
  }, [review.date])

  const locationText = useMemo(() => {
    if (!review.locationZipcode) return ''

    return `${review.locationZipcode}`
  }, [review.locationZipcode])

  if (type === 'section') {
    return (
      <>
        <div className="flex w-full min-w-0 flex-col gap-2">
          <div className="flex flex-1 flex-row items-center gap-3">
            <ProfileAvatar
              userId={-1}
              size={isMobile ? 40 : 45}
              className="border-2 border-white shadow-[0_1px_6px_1px_rgba(27,48,41,0.15)] xs:border-none xs:shadow-none"
            />
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex flex-row items-center justify-between xs:items-end">
                <div className="text-[15px] font-bold leading-none xs:text-[16px]">
                  {review.reviewerFullName || 'Anonymous'}
                </div>
                {/* Triple dots button. May need for report button. */}
                {/* <DotsButton numberOfDots={4} onClick={onExpand} /> */}
              </div>
              <div className="flex flex-row items-center gap-1">
                <span className="mr-1 flex items-center gap-1">
                  {[...Array(getNumberOfStars(review.rating ?? 0))].map((_, index) => (
                    <ReviewStar key={index} size={isMobile ? 12 : 15} pattern="full_black" />
                  ))}
                </span>
                <span className="line-clamp-1 text-[13px] text-muted xs:text-sm">
                  {locationText}
                  <span className="px-1">{'·'}</span>
                  {dateText}
                </span>
              </div>
            </div>
          </div>
          {/* Review description and read more button */}
          <div>
            <p
              ref={descriptionBoxRef}
              className={cn(
                'mt-2 text-[14px] leading-[1.65] text-primary xs:text-[16px] xs:leading-[1.6]',
                expandDescription ? 'line-clamp-none' : 'line-clamp-6',
                descriptionOverflow || expandDescription ? 'cursor-pointer' : '',
              )}
              onClick={() => {
                if (descriptionOverflow && !expandDescription) {
                  onExpand()
                } else {
                  onCollapse()
                }
              }}
            >
              {review.description || ''}
            </p>
            {descriptionOverflow && !expandDescription && (
              <button
                onClick={onExpand}
                className="mt-[6px] line-clamp-1 inline text-[14px] font-semibold leading-[1.6] text-primary underline xs:text-[16px]"
              >
                Read more
              </button>
            )}
            {expandDescription && (
              <button
                onClick={onCollapse}
                className="mt-[6px] line-clamp-1 inline text-[14px] font-semibold leading-[1.6] text-primary underline xs:text-[16px]"
              >
                Read less
              </button>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className="w-full min-w-[280px] max-w-[calc(100vw-40px)] rounded-2xl border border-[#efefef] bg-white p-4 md:min-w-[325px] md:max-w-none"
        style={{
          boxShadow: '0px 3px 10px 1px rgba(27, 48, 41, 0.03)',
        }}
      >
        <div className="flex items-center gap-3">
          <img src={ProfilePhoto} alt="" className="size-[35px] rounded-full object-cover md:size-[50px]" />
          <div>
            <div className="text-[13px] font-semibold text-black md:text-base">
              {review.reviewerFullName || 'Anonymous'}
            </div>
            <div className="text-[11px] text-muted md:text-xs">{descriptionText}</div>
          </div>
        </div>
        <TruncatedText
          text={review.description || ''}
          numberOfLines={3}
          customOverflowComponent={
            <button
              onClick={onExpand}
              className="line-clamp-1 inline pl-1 text-[12px] font-semibold leading-[1.6] text-black underline md:text-[14px]"
            >
              Read more
            </button>
          }
          className="my-3 w-full text-[13px] leading-[1.6] text-black md:text-[15px]"
        />
        <div className="mt-3 flex flex-row items-center gap-1.5 text-xs text-muted md:text-sm">
          <MiniZillow className="size-[13px] md:size-[15px]" />
          <span className="flex flex-row gap-0.5">
            {[...Array(getNumberOfStars(review.rating ?? 0))].map((_, index) => (
              <ReviewStar key={index} className="size-[13px] md:size-[15px]" pattern="gold" />
            ))}
          </span>
          <span>{' · '}</span>
          <span>{dateText}</span>
        </div>
      </div>
    </>
  )
}

export default PublicPageReviewCard
